import { addToast, Button, Modal, SelectOptionType } from '@octano/global-ui';
import { Col, Row } from 'reactstrap';
import { useForm } from 'react-hook-form';
import { FormValues } from '../../types';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import useActions from '../../hooks/useActions';
import { SectionTitle } from '../../../../../../components/text';
import { defaultValues, formatEditRecord, submitValues } from './utils';
import { FormFields } from './FormFields';

interface OtherStudiesFormProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  recordToEdit: any | undefined;
  closeModal: () => void;
  studentId: number;
  refresh: () => Promise<void>;
  academicLevels: SelectOptionType[];
  countries: SelectOptionType[];
  institutions: SelectOptionType[];
  loading: boolean;
}

export const OtherStudiesForm = ({
  isOpen,
  setIsOpen,
  recordToEdit,
  closeModal,
  studentId,
  refresh,
  academicLevels,
  countries,
  institutions: institutionsProp,
  loading,
}: OtherStudiesFormProps) => {
  const { t } = useTranslation();

  const texts = useMemo(() => {
    const prefix = 'studentManagement';

    return {
      title: t(`${prefix}.educationalInformation.form.title`),
      secondaryTitle: t(`${prefix}.educationalInformation.secondaryTitle`),
      buttonText: t(`${prefix}.educationalInformation.buttonText`),
      success: t(`${prefix}.educationalInformation.form.success`),
      error: t(`${prefix}.educationalInformation.form.error`),
    };
  }, [t]);

  const {
    createOtherStudy,
    updateOtherStudy,
    loading: isSubmitting,
  } = useActions({
    onSuccess: (type: string) => {
      if (type === 'create' || type === 'update') {
        setIsOpen(false);
        addToast({
          icon: 'success',
          color: 'success',
          text: texts.success,
        });
        refresh();
      }
    },
    onError: (_, message) => {
      addToast({
        icon: 'error',
        color: 'danger',
        text: message ?? texts.error,
      });
    },
  });

  const { handleSubmit, control, reset, watch, setValue } = useForm<FormValues>(
    {
      mode: 'onChange',
    },
  );

  const [countryValue, institutionValue] = watch(['country', 'institution']);

  const institutions = useMemo(
    () =>
      institutionsProp?.filter(
        (e) => !!countryValue?.value && countryValue?.value === e.countryId,
      ) || [],
    [institutionsProp, countryValue?.value],
  );

  const submit = (data: FormValues) => {
    if (!data?.institution) {
      return;
    }
    if (recordToEdit) {
      updateOtherStudy(recordToEdit.id, submitValues(data, studentId));
    } else {
      createOtherStudy(submitValues(data, studentId));
    }
  };

  useEffect(() => {
    if (isOpen && recordToEdit) {
      reset(formatEditRecord(recordToEdit));
    }

    return () => {
      reset({ ...defaultValues });
    };
  }, [isOpen, reset, recordToEdit]);

  const handleDownloadFile = (key: string) => {
    const file = recordToEdit?.files?.find(
      (item: { type: string }) => item.type === key,
    );

    if (!file) {
      return;
    }

    window.open(file.url, '_blank');
  };

  useEffect(() => {
    if (
      isOpen &&
      countryValue?.value &&
      institutionValue?.countryId &&
      institutionValue?.countryId !== countryValue?.value
    ) {
      setValue('institution', null);
    }
  }, [
    isOpen,
    countryValue?.value,
    institutionValue?.value,
    institutionValue?.countryId,
    setValue,
  ]);

  return (
    <Row className="mb-4 d-flex justify-content-between mt-4">
      <Col xs={12} md={6} className="d-flex align-items-center">
        <SectionTitle text={texts.secondaryTitle} />
      </Col>
      <Col xs={12} md={6} lg={3} className="d-flex justify-content-end">
        <Col>
          <Button
            text={texts.buttonText}
            outlined
            onClick={() => setIsOpen(true)}
            size="lg"
            fullwidth
            disabled={loading}
          />

          <Modal isOpen={isOpen} toggle={closeModal} size="lg">
            <div className="d-flex flex-column align-items-center">
              <h4>{texts.title}</h4>
              <form onSubmit={handleSubmit(submit)} className="w-100 mt-4">
                <FormFields
                  academicLevels={academicLevels}
                  countries={countries}
                  institutions={institutions}
                  control={control}
                  isSubmitting={isSubmitting}
                  closeModal={closeModal}
                  handleDownloadFile={handleDownloadFile}
                  watch={watch}
                />
              </form>
            </div>
          </Modal>
        </Col>
      </Col>
    </Row>
  );
};
